import * as $ from 'jquery';

import { CustomComponent } from '../common';

const ATTR_SELECT_ALL = 'iw-select-all';
const COMPONENT_SELECTOR = `input[type=checkbox][${ATTR_SELECT_ALL}]`;

interface SelectAllHTMLElement extends HTMLInputElement {
  controlledSelector: string;
}

declare global {
  interface JQuery {
    iwSelectAll(): JQuery<SelectAllHTMLElement>;
  }
}

/**
 * A jQuery plugin component that allows a checkbox input with the iw-select-all attribute to automatically select
 * or deselect all the checkbox inputs defined by the selector in the attribute value.
 *
 * @example
 *
 * ```html
 * <!-- feature.html -->
 * <input id="include-all" type="checkbox" iw-select-all="[name$=include]" />
 *
 * <input type="checkbox" name="form-0-include" />
 * <input type="checkbox" name="form-1-include" />
 * <input type="checkbox" name="form-2-include" />
 * <input type="checkbox" name="form-3-include" />
 * ```
 *
 * ```typescript
 * # feature.ts
 * $('#include-all').iwSelectAll();
 * ```
 */
export class SelectAllComponent extends CustomComponent<HTMLInputElement> {
  public static get COMPONENT_SELECTOR() {
    return COMPONENT_SELECTOR;
  }

  public static get ID_PREFIX(): string {
    return ATTR_SELECT_ALL;
  }

  public static loadPlugin(): void {
    super.loadPlugin();
    if (!$.fn.iwSelectAll) {
      $.fn.iwSelectAll = SelectAllComponent.jQueryPlugin('SelectAllComponent');
    }
  }

  public get controlledSelector(): string {
    return this.$el.attr(ATTR_SELECT_ALL) + `:not(#${this.id})`;
  }
  public set controlledSelector(value: string) {
    this.$el.attr(ATTR_SELECT_ALL, value);
  }

  public get indeterminate(): boolean {
    return this.someControlledChecked && !this.allControlledChecked;
  }

  public get selectAllChecked(): boolean {
    return this.$el.prop('checked');
  }

  public get allControlledChecked(): boolean {
    return this.$controlled.filter(':checked').length === this.$controlled.length;
  }

  public get someControlledChecked(): boolean {
    return this.$controlled.filter(':checked').length > 0;
  }

  public readonly $controlled: JQuery<HTMLInputElement>;

  constructor($el: JQuery<HTMLInputElement>) {
    super($el);

    this.$controlled = $(this.controlledSelector);

    this.$el.on('click', () => this.onSelectAllClick());
    this.$controlled.on('click', () => this.onControlledClick());

    // define these property setters/getters so that they can be accessed via jQuery.prop
    Object.defineProperties(this.el, {
      controlledSelector: {
        get: () => this.controlledSelector,
        set: (value: string) => this.controlledSelector = value,
      },
    });
  }

  protected onSelectAllClick(): void {
    this.$controlled.prop('checked', this.selectAllChecked);
  }

  protected onControlledClick(): void {
    this.$el
      .prop('checked', this.allControlledChecked)
      .prop('indeterminate', this.someControlledChecked && !this.allControlledChecked);
  }
}
